import { BigDecimal } from "@solomon-labs/bigdecimal";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";

import { isCurrentUsdvMint, isLegacyUsdvMint, LEGACY_USDV_DECIMALS, USDV_DECIMALS } from "../../tokens/usdv";
import type { TokenBalances } from "../types/balances";

export interface ParsedTokenAccount {
    mint: string;
    amount: string;
}

/**
 * Each tracked token's mint decimals: the one source for reading raw
 * amounts here and for showing balances at full precision.
 */
export const TOKEN_BALANCE_DECIMALS: Record<keyof TokenBalances, number> = {
    usdv: USDV_DECIMALS,
    legacyUsdv: LEGACY_USDV_DECIMALS,
    usdc: 6,
    usdt: 6,
    susdv: 9,
    // The SOLO mint is 6 decimals (getTokenSupply). 9 showed balances 1,000x small.
    solo: 6
};

/** SOLO token mint address (not in @solomon-labs/constants) */
const SOLO_MINT_ADDRESS = "SoLo9oxzLDpcq1dpqAgMwgce5WqkRDtNXK7EPnbmeta";

export function parseTokenBalances(accounts: ParsedTokenAccount[]): TokenBalances {
    const balances: TokenBalances = {
        usdv: null,
        legacyUsdv: null,
        usdc: null,
        usdt: null,
        susdv: null,
        solo: null
    };

    for (const account of accounts) {
        if (!isParsedTokenAccount(account)) {
            continue;
        }

        const { mint, amount } = account;
        const rawAmount = new BigDecimal(amount);

        if (isCurrentUsdvMint(mint)) {
            balances.usdv = addBalance(balances.usdv, rawAmount, TOKEN_BALANCE_DECIMALS.usdv);
        } else if (isLegacyUsdvMint(mint)) {
            balances.legacyUsdv = addBalance(balances.legacyUsdv, rawAmount, TOKEN_BALANCE_DECIMALS.legacyUsdv);
        } else if (mint === WELL_KNOWN_TOKENS_STRINGS.USDC) {
            balances.usdc = addBalance(balances.usdc, rawAmount, TOKEN_BALANCE_DECIMALS.usdc);
        } else if (mint === WELL_KNOWN_TOKENS_STRINGS.USDT) {
            balances.usdt = addBalance(balances.usdt, rawAmount, TOKEN_BALANCE_DECIMALS.usdt);
        } else if (mint === WELL_KNOWN_TOKENS_STRINGS.sUSDv) {
            balances.susdv = addBalance(balances.susdv, rawAmount, TOKEN_BALANCE_DECIMALS.susdv);
        } else if (mint === SOLO_MINT_ADDRESS) {
            balances.solo = addBalance(balances.solo, rawAmount, TOKEN_BALANCE_DECIMALS.solo);
        }
    }

    return balances;
}

function isParsedTokenAccount(account: unknown): account is ParsedTokenAccount {
    if (typeof account !== "object" || account === null) {
        return false;
    }

    const { mint, amount } = account as Record<string, unknown>;
    return typeof mint === "string" && mint.length > 0 && typeof amount === "string" && /^\d+$/.test(amount);
}

function addBalance(current: BigDecimal | null, rawAmount: BigDecimal, decimals: number): BigDecimal {
    const amount = rawAmount.divide(new BigDecimal(10).pow(decimals));
    return current === null ? amount : current.add(amount);
}

export function parseMockBalance(value: string | null | undefined): BigDecimal | null {
    if (value === null || value === undefined) {
        return null;
    }

    return new BigDecimal(value);
}
